import { Component, Input, OnInit, ViewChild } from '@angular/core';
import * as GC from '@grapecity/spread-sheets';
import { mapActionKeys } from '@helpers/spread/map-action-keys';
import { copyValues } from '@helpers/spread/sheet-functions';
import { MenuService } from '@services/menu/menu.service';
import { setDefaultBookOptions, setDefaultSheetOptions } from '@shared/spreadsheet/base';
import { resultsSpreadConfig } from '../sheet-builders/results-spread-config';

@Component({
  selector: 'app-error-display',
  templateUrl: './error-display.component.html',
  styleUrls: ['./error-display.component.scss'],
})
export class ErrorDisplayComponent implements OnInit {
  public spreadBackColor = resultsSpreadConfig.backColor; // Fix 3532 Part 3
  public hostStyle = resultsSpreadConfig.hostStyle;

  @ViewChild('spreadErrorsContainer', { static: false }) spreadContainer;
  @ViewChild('spreadErrorsDiv', { static: false }) spreadDiv;

  private spread: GC.Spread.Sheets.Workbook = null;
  private sheet: GC.Spread.Sheets.Worksheet = null;

  @Input() errorList;
  constructor(private menuservice: MenuService) {}

  ngOnInit() {}

  public workbookInit(args) {
    this.spread = args.spread;
    this.setupWorkBook();
  }

  private setupWorkBook() {
    this.menuservice.emitResize();

    this.sheet = this.spread.getActiveSheet();
    this.sheet.suspendPaint();

    // =========================
    //   General Sheet Options
    // =========================

    this.sheet.name('Error List');
    this.sheet.setColumnCount(1);
    this.sheet.defaults.rowHeight = 20;
    this.sheet.defaults.colWidth = 100;

    // Book and Sheet Options
    setDefaultBookOptions(this.spread.options);
    setDefaultSheetOptions(this.sheet.options);

    this.sheet.options.isProtected = true;
    this.sheet.options.protectionOptions.allowFilter = true;
    this.sheet.options.protectionOptions.allowSort = true;
    this.sheet.options.protectionOptions.allowSelectLockedCells = true;

    // Setup databinding
    this.setPageColumns(this.mapErrors(this.errorList));

    //defect 5829
    let filter: GC.Spread.Sheets.Filter.HideRowFilter = new GC.Spread.Sheets.Filter.HideRowFilter(
      new GC.Spread.Sheets.Range(0, 0, this.sheet.getRowCount(), this.sheet.getColumnCount())
    ).filterDialogVisibleInfo({
      sortByValue: true,
      sortByColor: false,
      filterByColor: false,
      filterByValue: true,
      listFilterArea: true,
    });
    this.sheet.rowFilter(filter);
    //------------------End Defect 5829

    mapActionKeys(this.spread, this.sheet, null, false, false);
    this.setupContextMenu();

    this.sheet.resumePaint();
  }

  private setupContextMenu() {
    const spreadFilter = this.spread.contextMenu.menuData.find(item => item.name === 'gc.spread.filter');

    const customCopy = {
      text: 'Copy',
      name: 'customCopy',
      command: copyValues,
      iconClass: 'gc-spread-copy',
      workArea: 'viewportcornercolHeaderrowHeader',
    };

    this.spread.contextMenu.menuData = [customCopy, spreadFilter];
  }

  private setPageColumns(data) {
    if (this.sheet) {
      this.sheet.autoGenerateColumns = true;
      this.sheet.setDataSource(data);
      this.sheet.bindColumns([
        { name: 'resultset', displayName: 'ResultSet' },
        {
          name: 'objectType',
          displayName: 'Object Type',
        },
        { name: 'object', displayName: 'Object' },
        { name: 'period', displayName: 'Period', size: 70 },
        { name: 'row', displayName: 'Row', size: 70 },
        { name: 'column', displayName: 'Column', size: 70 },
        { name: 'message', displayName: 'Message' },
        { name: 'severity', displayName: 'Severity', size: 70 },
      ]);

      var cellrange = new GC.Spread.Sheets.Range(-1, 0, -1, 8);
      var hideRowFilter = new GC.Spread.Sheets.Filter.HideRowFilter(cellrange);
      this.sheet.rowFilter(hideRowFilter);

      this.spread.options.autoFitType = GC.Spread.Sheets.AutoFitType.cellWithHeader;
      [0, 1, 2, 3, 4, 5, 6, 7].forEach(i => this.sheet.autoFitColumn(i));
    }
  }

  setupHeaders = (sheet: GC.Spread.Sheets.Worksheet) => {
    sheet.setColumnCount(8, GC.Spread.Sheets.SheetArea.colHeader);
    sheet.setValue(0, 0, 'ResultSet', GC.Spread.Sheets.SheetArea.colHeader);
    sheet.setValue(0, 1, 'Object Type', GC.Spread.Sheets.SheetArea.colHeader);
    sheet.setValue(0, 2, 'Object', GC.Spread.Sheets.SheetArea.colHeader);
    sheet.setValue(0, 3, 'Period', GC.Spread.Sheets.SheetArea.colHeader);
    sheet.setValue(0, 4, 'Row', GC.Spread.Sheets.SheetArea.colHeader);
    sheet.setValue(0, 5, 'Column', GC.Spread.Sheets.SheetArea.colHeader);
    sheet.setValue(0, 6, 'Message', GC.Spread.Sheets.SheetArea.colHeader);
    sheet.setValue(0, 7, 'Severity', GC.Spread.Sheets.SheetArea.colHeader);
  };

  public mapErrors(errorList): ErrorDisplay[] {
    const result = [];

    errorList.forEach(function (value) {
      if (value.type === 1) {
        let count = value.property.mnemonic === 'EndBal' ? -1 : 0;
        value.errors.forEach(error => {
          let message: string = error.message;
          let item: ErrorDisplay = {
            resultset: value.mnemonic,
            objectType: 'Balance Item',
            object: `${value.balance} : ${value.property.mnemonic}`,
            period: value.property.period + count,
            row: error.row + 1,
            column: String.fromCharCode(94 + (3 + error.column)).toLocaleUpperCase(),
            message: error.message,
            severity: message.toLowerCase().includes('capped') || message.toLowerCase().includes('floored') ? 'Warning' : 'Error',
          };
          result.push(item);
        });
      } else {
        let objTypeString;
        switch (value.type) {
          case 2:
            objTypeString = 'Liquidity Source';
            break;
          case 3:
            objTypeString = 'Exogenous Line';
            break;
          default:
            throw 'Object type not found in error string';
        }
        value.errors.forEach(error => {
          let message: string = error.message;
          const item: ErrorDisplay = {
            resultset: value.mnemonic,
            objectType: objTypeString,
            object: value.balance,
            period: value.property.period,
            row: error.row + 1,
            column: String.fromCharCode(94 + (3 + error.column)).toLocaleUpperCase(),
            message: error.message,
            severity: message.toLowerCase().includes('capped') || message.toLowerCase().includes('floored') ? 'Warning' : 'Error',
          };
          result.push(item);
        });
      }
    });
    return result;
  }
}

interface ErrorDisplay {
  resultset: string;
  objectType: string;
  object: string;
  period: number;
  row: number;
  column: string;
  message: string;
  severity: string;
}
